import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { CustomerSplit, PlatformKey, StoreAnalytics, StoreResponse } from '@clarity/contract';
import { useCustomerSplit, useStoreAnalytics } from '../queries/index.js';
import { useScopeContextOptional } from '../lib/scopeContext.js';
import { num } from './format.js';

/**
 * DashboardOperations (clarity-owner-analytics) — the operational glance beneath
 * the money band: ratings, delivery time, and the new-vs-returning split across
 * the in-scope branches. Built for a multi-branch / multi-channel owner: each
 * metric rolls up over the branches that HAVE it recorded, and says how many
 * branches are missing it (so a blank is honest, never a fake zero).
 *
 * Each branch's reads run in a hidden source component (one hooks instance per
 * branch, reporting up) — the same fan-out pattern as DashboardBalance, so the
 * rules of hooks hold for a variable branch set. Reads are cheap tenant-scoped
 * repo reads sharing the query cache; no portal fetch.
 */

const PLATFORM_LABEL: Record<PlatformKey, string> = {
  talabat: 'Talabat',
  careem: 'Careem',
  mythings: 'MyThings',
};

function thisMonthWindow(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
  return { from, to: now.toISOString() };
}

interface BranchOps {
  analytics: StoreAnalytics | null;
  split: CustomerSplit | null;
  loading: boolean;
}

export function DashboardOperations({
  branches,
  linkToInsights = false,
}: {
  branches: StoreResponse[];
  /** Show a "View all insights" link to the scope's Insights surface (dashboard only). */
  linkToInsights?: boolean;
}) {
  const { clientId } = useParams();
  const scopeCtx = useScopeContextOptional();
  // The Insights href for the CURRENT scope (all / brand / store), so the glance
  // deep-links to the same scope's full page. Only built when asked + resolvable.
  const insightsHref =
    linkToInsights && clientId && scopeCtx
      ? scopeCtx.scope.kind === 'brand'
        ? `/c/${clientId}/brand/${scopeCtx.scope.brandId}/insights`
        : scopeCtx.scope.kind === 'store'
          ? `/c/${clientId}/store/${scopeCtx.scope.storeId}/insights`
          : `/c/${clientId}/all/insights`
      : null;
  const onboarded = useMemo(() => branches.filter((b) => b.onboardingComplete), [branches]);
  const win = useMemo(() => thisMonthWindow(), []);
  const [byBranch, setByBranch] = useState<Record<string, BranchOps>>({});
  const report = useCallback((id: string, ops: BranchOps) => {
    setByBranch((prev) => (prev[id] && prev[id]!.loading === ops.loading && prev[id]!.analytics === ops.analytics && prev[id]!.split === ops.split ? prev : { ...prev, [id]: ops }));
  }, []);

  const roll = useMemo(() => {
    let ratingSum = 0;
    let ratingWeight = 0;
    let ratingBranches = 0;
    let deliverySum = 0;
    let deliveryBranches = 0;
    let availSum = 0;
    let availBranches = 0;
    let orders = 0;
    let newCust = 0;
    let anySplit = false;
    for (const b of onboarded) {
      const ops = byBranch[b.id];
      if (!ops) continue;
      const a = ops.analytics;
      if (a?.ratingAvg != null) {
        const w = a.ratingCount ?? 1;
        ratingSum += a.ratingAvg * w;
        ratingWeight += w;
        ratingBranches += 1;
      }
      // Prefer delivery time; fall back to prep time when delivery isn't reported
      // (Talabat/Careem report prep, not door-to-door).
      const deliveryOrPrep = a?.avgDeliveryMinutes ?? a?.avgPrepMinutes ?? null;
      if (deliveryOrPrep != null) {
        deliverySum += deliveryOrPrep;
        deliveryBranches += 1;
      }
      if (a?.availabilityPct != null) {
        availSum += a.availabilityPct;
        availBranches += 1;
      }
      const s = ops.split;
      if (s) {
        orders += s.orders;
        if (s.basis === 'deal') {
          anySplit = true;
          newCust += s.newCustomerOrders ?? 0;
        }
      }
    }
    return {
      rating: ratingWeight > 0 ? ratingSum / ratingWeight : null,
      ratingBranches,
      avgDelivery: deliveryBranches > 0 ? deliverySum / deliveryBranches : null,
      deliveryBranches,
      availability: availBranches > 0 ? availSum / availBranches : null,
      availBranches,
      orders,
      newCust,
      anySplit,
    };
  }, [onboarded, byBranch]);

  if (onboarded.length === 0) return null;
  const total = onboarded.length;
  const withFigures = onboarded.filter((b) => byBranch[b.id]?.analytics);
  const anyLoading = onboarded.some((b) => !byBranch[b.id] || byBranch[b.id]!.loading);

  return (
    <section className="card dash-ops" aria-label="Operations">
      {onboarded.map((b) => (
        <BranchOpsSource key={b.id} store={b} win={win} onResult={report} />
      ))}
      <header className="dash-ops__head">
        <h3>Operations this month</h3>
        {insightsHref && (
          <Link className="dash-ops__more" to={insightsHref}>
            View all insights →
          </Link>
        )}
      </header>
      <div className="dash-ops__grid">
        <OpsStat label="Avg rating" value={roll.rating != null ? roll.rating.toFixed(1) : '—'} sub={roll.rating != null ? `across ${roll.ratingBranches} of ${total} branches` : 'not recorded yet'} />
        <OpsStat label="Avg delivery / prep" value={roll.avgDelivery != null ? `${Math.round(roll.avgDelivery)} min` : '—'} sub={roll.avgDelivery != null ? `across ${roll.deliveryBranches} of ${total} branches` : 'not recorded yet'} />
        <OpsStat label="Availability" value={roll.availability != null ? `${roll.availability.toFixed(1)}%` : '—'} sub={roll.availability != null ? `online time · ${roll.availBranches} of ${total} branches` : 'not recorded yet'} />
        <OpsStat label="New-customer orders" value={roll.anySplit ? num(roll.newCust) : '—'} sub={roll.anySplit ? `of ${num(roll.orders)} orders (Talabat deals)` : 'not reported by these platforms'} />
        <OpsStat label="Returning orders" value={roll.anySplit ? num(Math.max(0, roll.orders - roll.newCust)) : num(roll.orders)} sub={roll.anySplit ? 'the rest of this month\u2019s orders' : 'total orders this month'} />
      </div>
      {!anyLoading && roll.rating == null && roll.avgDelivery == null && roll.availability == null && (
        <p className="dash-ops__hint">
          Record ratings, delivery time, opening hours and conversion per branch to track them here and
          ask the advisor about them. Open a branch to add them.
        </p>
      )}
      {withFigures.length > 0 && (
        <ul className="dash-ops__branches">
          {withFigures.map((b) => {
            const a = byBranch[b.id]!.analytics!;
            const conv = conversionRate(a);
            return (
              <li key={b.id} className="dash-ops__branch">
                <span className="dash-ops__branch-name">
                  {b.name ?? PLATFORM_LABEL[b.platformKey as PlatformKey]}
                  <span className="dash-ops__chan">{PLATFORM_LABEL[b.platformKey as PlatformKey]}</span>
                </span>
                <span className="dash-ops__branch-metrics">
                  {a.ratingAvg != null && <span>★ {a.ratingAvg.toFixed(1)}</span>}
                  {(a.avgDeliveryMinutes ?? a.avgPrepMinutes) != null && <span>{Math.round((a.avgDeliveryMinutes ?? a.avgPrepMinutes)!)} min</span>}
                  {a.availabilityPct != null && <span>{a.availabilityPct.toFixed(0)}% up</span>}
                  {conv != null && <span>{conv.toFixed(1)}% conv.</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function conversionRate(a: StoreAnalytics | null): number | null {
  if (!a) return null;
  const visits = a.menuVisits ?? a.impressions;
  if (!visits || visits <= 0 || a.conversions == null) return null;
  return (a.conversions / visits) * 100;
}

function OpsStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value num">{value}</span>
      <span className="stat__sub">{sub}</span>
    </div>
  );
}

/** One hidden per-branch reader (analytics record + customer split) reporting up. */
function BranchOpsSource({
  store,
  win,
  onResult,
}: {
  store: StoreResponse;
  win: { from: string; to: string };
  onResult: (id: string, ops: BranchOps) => void;
}) {
  const analytics = useStoreAnalytics(store.id);
  const split = useCustomerSplit(store.id, win.from, win.to);
  const records = analytics.data?.status === 200 ? analytics.data.body : [];
  const record = records.find((r) => r.periodEnd >= win.from && r.periodStart < win.to) ?? records[0] ?? null;
  const splitBody = split.data?.status === 200 ? split.data.body : null;
  const loading = analytics.isLoading || split.isLoading;
  useEffect(() => {
    onResult(store.id, { analytics: record, split: splitBody, loading });
  }, [store.id, record, splitBody, loading, onResult]);
  return null;
}
